import { Badge, Button, DataTable, type DataTableColumn, FormField, Modal, Textarea, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AdminConnectorSummary } from "@/api";
import {
  adminConnectorAuditQueryKey,
  adminDisableConnectorMutation,
  adminEnableConnectorMutation,
  adminListConnectorsOptions,
  adminListConnectorsQueryKey,
  errorMessage,
} from "@/api";
import { PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";

function StatusBadge({ connector }: Readonly<{ connector: AdminConnectorSummary }>) {
  const { t } = useTranslation("adminIntegrations");
  if (connector.disabledByAdmin) return <Badge variant="danger">{t("connectors.status.disabled")}</Badge>;
  if (!connector.hostAllowed) return <Badge variant="warning">{t("connectors.status.hostBlocked")}</Badge>;
  if (connector.lastError) return <Badge variant="warning">{t("connectors.status.problem")}</Badge>;
  return connector.isEnabled ? (
    <Badge variant="success">{t("connectors.status.on")}</Badge>
  ) : (
    <Badge>{t("connectors.status.off")}</Badge>
  );
}

function DisableDialog({ connector, onClose }: Readonly<{ connector: AdminConnectorSummary; onClose: () => void }>) {
  const { t } = useTranslation("adminIntegrations");
  const toast = useToast();
  const queryClient = useQueryClient();
  const id = useId();
  const [reason, setReason] = useState("");
  const disable = useMutation(adminDisableConnectorMutation());
  // Modal re-runs its focus effect whenever onClose changes; a stable one keeps focus in the reason box.
  const pending = disable.isPending;
  const close = useCallback(() => !pending && onClose(), [pending, onClose]);
  const submit = () =>
    disable.mutate(
      { path: { connector_uuid: connector.uuid }, body: { reason: reason.trim() || null } },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: adminListConnectorsQueryKey() }),
            queryClient.invalidateQueries({ queryKey: adminConnectorAuditQueryKey() }),
          ]);
          toast.success(t("connectors.disabled", { name: connector.name }));
          onClose();
        },
        onError: (error) => toast.error(errorMessage(error, t("connectors.actionFailed", { name: connector.name }))),
      },
    );
  return (
    <Modal
      open
      onClose={close}
      title={t("connectors.disableTitle", { name: connector.name })}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={disable.isPending}>
            {t("edit.cancel")}
          </Button>
          <Button variant="danger" loading={disable.isPending} onClick={submit}>
            {t("connectors.disable")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Text variant="body-sm" tone="secondary">
          {t("connectors.disableBody")}
        </Text>
        <FormField label={t("connectors.reason")} htmlFor={id}>
          <Textarea id={id} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </FormField>
      </div>
    </Modal>
  );
}

/** Every user-added connector; platform admins turn one off for everyone, or back on (D16). */
export function AdminConnectorsSection({ canEdit }: Readonly<{ canEdit: boolean }>) {
  const { t } = useTranslation("adminIntegrations");
  const toast = useToast();
  const queryClient = useQueryClient();
  const connectors = useQuery({ ...adminListConnectorsOptions(), select: (r) => r.data });
  const enable = useMutation(adminEnableConnectorMutation());
  const [disabling, setDisabling] = useState<AdminConnectorSummary | null>(null);

  const turnOn = (connector: AdminConnectorSummary) =>
    enable.mutate(
      { path: { connector_uuid: connector.uuid } },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: adminListConnectorsQueryKey() }),
            queryClient.invalidateQueries({ queryKey: adminConnectorAuditQueryKey() }),
          ]);
          toast.success(t("connectors.enabled", { name: connector.name }));
        },
        onError: (error) => toast.error(errorMessage(error, t("connectors.actionFailed", { name: connector.name }))),
      },
    );

  const columns: DataTableColumn<AdminConnectorSummary>[] = [
    {
      key: "name",
      header: t("connectors.columns.name"),
      mobile: "primary",
      render: (c) => (
        <div className="flex min-w-0 flex-col gap-0.5 text-left">
          <Text as="span" variant="body-sm" className="truncate">
            {c.name}
          </Text>
          <Text as="span" variant="caption" tone="tertiary" className="max-w-80 truncate">
            {c.url}
          </Text>
        </div>
      ),
    },
    {
      key: "owner",
      header: t("connectors.columns.owner"),
      render: (c) => (
        <Text as="span" variant="body-sm" tone="secondary">
          {c.createdByName ?? t("connectors.someone")}
        </Text>
      ),
    },
    {
      key: "tools",
      header: t("connectors.columns.tools"),
      render: (c) => (
        <Text as="span" variant="body-sm" tone="secondary" className="whitespace-nowrap">
          {t("toolCount", { count: c.toolCount ?? 0 })}
        </Text>
      ),
    },
    { key: "status", header: t("connectors.columns.status"), render: (c) => <StatusBadge connector={c} /> },
  ];

  return (
    <PageSection title={t("connectors.listTitle")} description={t("connectors.listBody")}>
      <DataTable
        aria-label={t("connectors.tableLabel")}
        columns={columns}
        rows={connectors.data ?? []}
        rowKey={(c) => c.uuid}
        loading={connectors.isPending}
        empty={
          <Text variant="body-sm" tone="tertiary">
            {t("connectors.empty")}
          </Text>
        }
        renderActions={
          canEdit
            ? (c) =>
                c.disabledByAdmin ? (
                  <Button variant="secondary" size="sm" onClick={() => turnOn(c)}>
                    {t("connectors.enable")}
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setDisabling(c)}>
                    {t("connectors.disable")}
                  </Button>
                )
            : undefined
        }
        actionsLabel={t("connectors.columns.actions")}
      />
      {disabling && <DisableDialog connector={disabling} onClose={() => setDisabling(null)} />}
    </PageSection>
  );
}
